import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: September 10, 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Introduction */}
        <section className="space-y-4">
          <p className="text-muted-foreground">
            UniSchedule ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our university scheduling platform.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Information We Collect</h2>
          </div>
          
          <div className="space-y-4 pl-7">
            <div>
              <h3 className="font-semibold mb-2">Personal Information</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Name (optional, for personalization)</li>
                <li>Email address (optional, for feedback responses)</li>
                <li>Academic information (program, department, semester, group)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Usage Information</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Timetable preferences and selections</li>
                <li>Feature usage patterns</li>
                <li>Device information and browser type</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">How We Use Your Information</h2>
          </div>
          
          <ul className="list-disc list-inside space-y-2 text-muted-foreground pl-7">
            <li>To provide and personalize your timetable experience</li>
            <li>To display relevant class schedules, room locations, and bus timings</li>
            <li>To improve our services and develop new features</li>
            <li>To respond to your feedback and support requests</li>
            <li>To send important updates about schedule changes (if notifications enabled)</li>
          </ul>
        </section>

        {/* Data Storage */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Data Storage and Security</h2>
          </div>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>
              Your preferences are stored locally in your browser using localStorage. This means:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Your saved timetable profile stays on your device</li>
              <li>Published Lost &amp; Found reports and photos are stored on our servers for everyone to see</li>
              <li>Clearing browser storage removes your saved profile and report management keys; it does not delete public reports</li>
            </ul>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Lost &amp; Found Community Reports</h2>
          <p className="text-muted-foreground">
            When you publish a report, its item details, photo, location, date, display name,
            and chosen contact information are public. You can choose a campus handover
            point instead of sharing an email address or phone number. Photos are resized
            before upload; please keep private details and ID numbers out of reports.
          </p>
          <p className="text-muted-foreground">
            Reports and photos expire after 90 days. You can resolve or remove a report
            from the browser that created it. Its management key is stored on that device;
            clearing browser storage does not delete the public report. Contact us if you
            need help removing a report after losing access to its management key.
          </p>
          <p className="text-muted-foreground">
            Vercel and Upstash host the shared board. Short-lived request counters use
            a hashed network identifier to limit spam; raw IP addresses are not stored
            in the board records.
          </p>
        </section>
        {/* Third-Party Services */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Third-Party Services</h2>
          </div>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>FormSubmit</strong> - For processing feedback form submissions</li>
              <li><strong>Google Fonts</strong> - For typography</li>
            </ul>
            <p className="text-sm">
              These services have their own privacy policies governing the use of your information.
            </p>
          </div>
        </section>

        {/* Your Rights */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Your Rights</h2>
          
          <div className="space-y-2 text-muted-foreground pl-7">
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Access your stored preferences</li>
              <li>Delete your data at any time</li>
              <li>Opt-out of optional features</li>
              <li>Request information about data we collect</li>
            </ul>
          </div>
        </section>

        {/* Children's Privacy */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Children's Privacy</h2>
          <p className="text-muted-foreground pl-7">
            UniSchedule is designed for university students (typically 18+). We do not knowingly collect information from children under 13.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Changes to This Policy</h2>
          <p className="text-muted-foreground pl-7">
            We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last updated" date at the top of this policy.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Contact Us</h2>
          <p className="text-muted-foreground pl-7">
            If you have questions about this Privacy Policy, please contact us at:{' '}
            <a href="mailto:faisalh5556@gmail.com" className="text-primary hover:underline">
              faisalh5556@gmail.com
            </a>
          </p>
        </section>

        {/* Back Button */}
        <div className="pt-8 border-t">
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
